import { BroadcastChannel } from 'broadcast-channel'
import {
  ROUTER_CATEGORY_GROUPS,
  ROUTER_FALLBACK_REASONS,
  type RouterCategoryGroup,
  type RouterDecision,
  type RouterFallbackReason,
} from '@dupe/common/affiliateRouter'
import type { AffiliateRouterArm } from '~/src/lib/affiliateRouter'

// Shared broadcast channel utility to prevent conflicts
class BroadcastChannelManager {
  private static instance: BroadcastChannelManager
  private channels: Map<string, BroadcastChannel> = new Map()

  private constructor() {}

  static getInstance(): BroadcastChannelManager {
    if (!BroadcastChannelManager.instance) {
      BroadcastChannelManager.instance = new BroadcastChannelManager()
    }
    return BroadcastChannelManager.instance
  }

  getChannel(name: string): BroadcastChannel {
    if (!this.channels.has(name)) {
      // Create new channel instance if one doesn't exist
      this.channels.set(name, new BroadcastChannel(name))
    }
    // Return existing or newly created channel instance
    return this.channels.get(name)!
  }

  closeChannel(name: string): void {
    const channel = this.channels.get(name)
    if (channel) {
      channel.close()
      this.channels.delete(name)
    }
  }

  closeAll(): void {
    this.channels.forEach((channel) => channel.close())
    this.channels.clear()
  }
}

// Export singleton instance
export const broadcastManager = BroadcastChannelManager.getInstance()

// Convenience functions
export const getBroadcastChannel = (name: string) =>
  broadcastManager.getChannel(name)
export const closeBroadcastChannel = (name: string) =>
  broadcastManager.closeChannel(name)

// Broadcast message interface
export interface BroadcastMessage {
  dupeCompareTo?: string
  productTitle?: string
  redirectUrl?: string
  routerDecision?: RouterDecision
  routerArm?: AffiliateRouterArm
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function optionalNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function isRouterCategoryGroup(value: unknown): value is RouterCategoryGroup {
  return ROUTER_CATEGORY_GROUPS.includes(value as RouterCategoryGroup)
}

function isRouterFallbackReason(value: unknown): value is RouterFallbackReason {
  return ROUTER_FALLBACK_REASONS.includes(value as RouterFallbackReason)
}

function optionalRouterArm(value: unknown): AffiliateRouterArm | undefined {
  return value === 'control' || value === 'test' ? value : undefined
}

/** Validates an untrusted `RouterDecision` crossing a boundary — a broadcast
 *  message, or the server's decision on the step 5 `rr=1` redirect response. */
export function parseRouterDecision(
  value: unknown
): RouterDecision | undefined {
  const record = asRecord(value)
  if (!record) return undefined

  const shopKey = optionalString(record.shopKey)
  const displayName = optionalString(record.displayName)
  const routerVersion = optionalString(record.routerVersion)
  if (!shopKey || !displayName || !routerVersion) return undefined

  const categoryGroup = isRouterCategoryGroup(record.categoryGroup)
    ? record.categoryGroup
    : null
  const fallbackReason = isRouterFallbackReason(record.fallbackReason)
    ? record.fallbackReason
    : null
  const department =
    record.department === 'womens' || record.department === 'mens'
      ? record.department
      : null

  // Tri-state, and the three states are NOT interchangeable: `true`/`false` are
  // real evaluations, `null` means no rule was consulted. A truthy check here
  // would silently fold `false` into `null` and delete exactly the half of the
  // readout the observe-mode rule exists to measure — so test for the literals.
  const eligibilityMatch =
    typeof record.eligibilityMatch === 'boolean'
      ? record.eligibilityMatch
      : null

  return {
    shopKey,
    displayName,
    categoryGroup,
    confidence: optionalNumber(record.confidence),
    department,
    fallbackReason,
    eligibilityMatch,
    routerVersion,
  }
}

// Helper to parse broadcast messages consistently
export const parseBroadcastMessage = (msg: MessageEvent): BroadcastMessage => {
  let dupeCompareTo: string | undefined
  let productTitle: string | undefined
  let redirectUrl: string | undefined
  let routerDecision: RouterDecision | undefined
  let routerArm: AffiliateRouterArm | undefined

  const messageData = asRecord(msg.data) ?? asRecord(msg)

  if (messageData) {
    dupeCompareTo = optionalString(messageData.dupeCompareTo)
    productTitle = optionalString(messageData.productTitle)
    redirectUrl = optionalString(messageData.redirectUrl)
    routerDecision = parseRouterDecision(messageData.routerDecision)
    routerArm = optionalRouterArm(messageData.routerArm)
  } else {
    dupeCompareTo = undefined
    productTitle = undefined
    redirectUrl = undefined
    routerDecision = undefined
    routerArm = undefined
  }

  return { dupeCompareTo, productTitle, redirectUrl, routerDecision, routerArm }
}
